import { ChipWarning, ChipWarningFocusTarget, ChipWarningKind, ChipWarningSeverity } from '@resistapp/common/types';
import { chain } from 'lodash';

export const CHIP_WARNING_SIDE_PANEL_MIN_WIDTH = 320;

export function getChipWarningPanelPlacement(graphColumnWidth: number, heatmapSide: number): 'side' | 'top' {
  return graphColumnWidth - heatmapSide >= CHIP_WARNING_SIDE_PANEL_MIN_WIDTH ? 'side' : 'top';
}

export function getChipWarningPanelWarnings(warnings: ChipWarning[]): ChipWarning[] {
  const groupedAssayTargets = getGroupedWarningFocusTargets(
    warnings,
    ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION,
    target => target.assay != null,
  );
  const groupedSampleTargets = getGroupedWarningFocusTargets(
    warnings,
    ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION,
    target => target.technicalSample != null,
  );
  const groupedSixteenSTargets = getGroupedWarningFocusTargets(
    warnings,
    ChipWarningKind.SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS,
    target => target.biologicalSample != null,
  );

  return warnings.reduce<ChipWarning[]>((acc, warning) => {
    if (warning.kind === ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION) {
      return acc.some(item => item.kind === ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION) ||
        groupedAssayTargets.length === 0
        ? acc
        : [
            ...acc,
            buildGroupedNoAmplificationWarning(ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION, groupedAssayTargets),
          ];
    }

    if (warning.kind === ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION) {
      return acc.some(item => item.kind === ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION) ||
        groupedSampleTargets.length === 0
        ? acc
        : [
            ...acc,
            buildGroupedNoAmplificationWarning(ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION, groupedSampleTargets),
          ];
    }

    if (warning.kind === ChipWarningKind.SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS) {
      return acc.some(item => item.kind === ChipWarningKind.SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS) ||
        groupedSixteenSTargets.length === 0
        ? acc
        : [...acc, buildGroupedSixteenSMissingWarning(groupedSixteenSTargets)];
    }

    return [...acc, normalizeChipWarningForPanel(warning)];
  }, []);
}

export function getNoAmplificationHighlights(warnings: ChipWarning[]): {
  flaggedAssays: string[];
  flaggedSamples: string[];
} {
  const flaggedAssays = warnings
    .filter(warning => warning.kind === ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION)
    .flatMap(warning => warning.focusTargets)
    .map(target => target.assay)
    .filter((assay): assay is string => assay != null);

  const flaggedSamples = warnings
    .filter(warning => warning.kind === ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION)
    .flatMap(warning => warning.focusTargets)
    .map(target => target.technicalSample)
    .filter((technicalSample): technicalSample is string => technicalSample != null);

  return {
    flaggedAssays: Array.from(new Set(flaggedAssays)),
    flaggedSamples: Array.from(new Set(flaggedSamples)),
  };
}

function buildGroupedNoAmplificationWarning(
  kind: ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION | ChipWarningKind.SAMPLE_WITHOUT_AMPLIFICATION,
  focusTargets: ChipWarningFocusTarget[],
): ChipWarning {
  const isAssayWarning = kind === ChipWarningKind.ASSAY_WITHOUT_AMPLIFICATION;
  const targetLabel =
    focusTargets.length === 1 ? (isAssayWarning ? 'assay' : 'sample') : isAssayWarning ? 'assays' : 'samples';

  return {
    id: isAssayWarning ? 'panel-assays-without-amplification' : 'panel-samples-without-amplification',
    kind,
    severity: ChipWarningSeverity.WARNING,
    message: `No amplification for ${targetLabel}:`,
    focusTargets,
  };
}

function buildGroupedSixteenSMissingWarning(focusTargets: ChipWarningFocusTarget[]): ChipWarning {
  return {
    id: 'panel-sixteen-s-missing-with-other-detections',
    kind: ChipWarningKind.SIXTEEN_S_MISSING_WITH_OTHER_DETECTIONS,
    severity: ChipWarningSeverity.WARNING,
    message: `16S (AY1) is not detected while other genes are detected for sample${focusTargets.length === 1 ? '' : 's'}:`,
    focusTargets,
  };
}

function normalizeChipWarningForPanel(warning: ChipWarning): ChipWarning {
  if (warning.kind === ChipWarningKind.HIGH_FLAG_COUNTS && warning.focusTargets.some(target => target.flag != null)) {
    return {
      ...warning,
      focusTargets: getUniqueSortedFocusTargets(warning.focusTargets.filter(target => target.flag != null)),
      message: 'High number of flags:',
    };
  }

  return warning;
}

function getGroupedWarningFocusTargets(
  warnings: ChipWarning[],
  kind: ChipWarningKind,
  predicate: (target: ChipWarningFocusTarget) => boolean,
): ChipWarningFocusTarget[] {
  return getUniqueSortedFocusTargets(
    warnings.filter(warning => warning.kind === kind).flatMap(warning => warning.focusTargets.filter(predicate)),
  );
}

function getUniqueSortedFocusTargets(focusTargets: ChipWarningFocusTarget[]): ChipWarningFocusTarget[] {
  return chain(focusTargets)
    .uniqBy(getChipWarningFocusTargetKey)
    .sortBy(target => target.label.toLowerCase())
    .value();
}

function getChipWarningFocusTargetKey(target: ChipWarningFocusTarget): string {
  return [target.label, target.assay, target.biologicalSample, target.technicalSample, target.flag].join('|');
}
